// Geometry for the verified badge: a scalloped circle ("seal") with a
// checkmark. Pure math (Metro-safe) so the web (SVG) and Expo
// (react-native-svg) components render an identical shape.
//
// Tuned values (approved via the design tuner): medium scallops at 35% of
// height, 11% depth, rounded bumps, and a light checkmark.

export interface VerifiedSeal {
  /** SVG element + viewBox width (square; includes the scallop margin). */
  width: number;
  /** SVG element + viewBox height (square; includes the scallop margin). */
  height: number;
  /** Path `d` for the filled scalloped-circle outline. */
  outline: string;
  /** Path `d` + stroke width for the checkmark. */
  check: { d: string; strokeWidth: number };
}

const SCALLOP_SIZE = 0.25; // scallop arc length as a fraction of height
const SCALLOP_DEPTH = 0.1; // scallop protrusion as a fraction of height
const CHECK_WEIGHT = 0.09; // check stroke width as a fraction of height
const CHECK_SCALE = 0.95; // checkmark size relative to the default glyph
const r2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Build the scalloped-circle outline as a single closed path: evenly-placed
 * rounded bumps around the perimeter, each bulging outward from the center.
 * `ox`/`oy` offset every coordinate by the scallop margin.
 */
function scallopedCircle(
  diameter: number,
  depth: number,
  targetArc: number,
  ox: number,
  oy: number,
): string {
  const r = diameter / 2;
  const cx = r;
  const cy = r;
  const rv = r - depth; // valley radius
  const nb = Math.max(8, Math.round((2 * Math.PI * rv) / targetArc));
  const pts: [number, number][] = [];
  for (let k = 0; k < nb; k++) {
    const t = -Math.PI / 2 + (2 * Math.PI * k) / nb;
    pts.push([cx + rv * Math.cos(t), cy + rv * Math.sin(t)]);
  }

  // Quadratic control pushed radially outward so the bump apex protrudes
  // ~depth past the valley to reach the outer edge.
  const ctrl = (a: [number, number], b: [number, number]): [number, number] => {
    const mx = (a[0] + b[0]) / 2;
    const my = (a[1] + b[1]) / 2;
    const dx = mx - cx;
    const dy = my - cy;
    const len = Math.hypot(dx, dy) || 1;
    return [mx + (dx / len) * depth * 2, my + (dy / len) * depth * 2];
  };

  let d = `M${r2(pts[0][0] + ox)} ${r2(pts[0][1] + oy)}`;
  for (let j = 0; j < nb; j++) {
    const a = pts[j];
    const b = pts[(j + 1) % nb];
    const c = ctrl(a, b);
    d += `Q${r2(c[0] + ox)} ${r2(c[1] + oy)} ${r2(b[0] + ox)} ${r2(b[1] + oy)}`;
  }
  return `${d}Z`;
}

export function scallopBadgeGeometry(height: number): VerifiedSeal {
  const h = height;
  const depth = h * SCALLOP_DEPTH;
  const targetArc = h * SCALLOP_SIZE;
  // Breathing room so the scallop peaks aren't clipped at the viewBox edge.
  const margin = depth * 1.3;
  const c = h / 2 + margin; // seal center (x and y), offset by margin

  const outline = scallopedCircle(h, depth, targetArc, margin, margin);
  const sw = Math.max(1.4, h * CHECK_WEIGHT);
  const k = CHECK_SCALE * h;
  const check = {
    d: `M${r2(c - 0.22 * k)} ${r2(c)} L${r2(c - 0.06 * k)} ${r2(c + 0.15 * k)} L${r2(c + 0.2 * k)} ${r2(c - 0.17 * k)}`,
    strokeWidth: r2(sw),
  };

  return {
    width: r2(h + 2 * margin),
    height: r2(h + 2 * margin),
    outline,
    check,
  };
}
